import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from 'react';

/**
 * Pan and zoom.
 *
 * Camera state is browser-local — it is a view, not data — so it never
 * reaches the vault and losing it costs nothing.
 *
 * No mode switch. The phone needs Navigate ⇄ Arrange because a finger cannot
 * say whether it means to pan or to move a card; a pointer can. Space-drag
 * and middle-drag pan, a drag on the canvas selects, a drag on a card moves
 * it, and scroll pans while ⌘-scroll zooms.
 */

export interface Camera {
  x: number;
  y: number;
  zoom: number;
}

export interface ZoomBounds {
  min: number;
  max: number;
}

const DEFAULT_BOUNDS: ZoomBounds = { min: 0.25, max: 2 };

export function useCamera(
  initial: Camera = { x: 0, y: 0, zoom: 1 },
  bounds: ZoomBounds = DEFAULT_BOUNDS,
) {
  const [camera, setCamera] = useState<Camera>(initial);
  const panning = useRef<{ x: number; y: number } | null>(null);
  const spaceHeld = useRef(false);
  const { min: minZoom, max: maxZoom } = bounds;

  // Space pans, so the key has to be tracked rather than read from an event.
  // Not while typing: a space in a field is a space.
  useEffect(() => {
    const typing = (event: KeyboardEvent) => {
      const el = event.target as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const down = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !typing(e)) spaceHeld.current = true;
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceHeld.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  const onWheel = useCallback((event: ReactWheelEvent) => {
    if (event.ctrlKey || event.metaKey) {
      // Zoom toward the pointer, so the thing under the cursor stays under it.
      const rect = event.currentTarget.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      setCamera((c) => {
        const zoom = clamp(c.zoom * (1 - event.deltaY / 400), minZoom, maxZoom);
        const k = zoom / c.zoom;
        return { zoom, x: px - (px - c.x) * k, y: py - (py - c.y) * k };
      });
      return;
    }
    setCamera((c) => ({ ...c, x: c.x - event.deltaX, y: c.y - event.deltaY }));
  }, [minZoom, maxZoom]);

  const beginPan = useCallback((event: ReactPointerEvent) => {
    panning.current = { x: event.clientX, y: event.clientY };
  }, []);

  const onPointerMove = useCallback((event: ReactPointerEvent) => {
    const from = panning.current;
    if (!from) return;
    const dx = event.clientX - from.x;
    const dy = event.clientY - from.y;
    panning.current = { x: event.clientX, y: event.clientY };
    setCamera((c) => ({ ...c, x: c.x + dx, y: c.y + dy }));
  }, []);

  const endPan = useCallback(() => {
    panning.current = null;
  }, []);

  const wantsPan = useCallback(
    (event: ReactPointerEvent) => event.button === 1 || spaceHeld.current,
    [],
  );

  return {
    camera,
    setCamera,
    onWheel,
    beginPan,
    onPointerMove,
    endPan,
    wantsPan,
    isPanning: () => panning.current !== null,
    zoomBy: (factor: number) =>
      setCamera((c) => ({ ...c, zoom: clamp(c.zoom * factor, minZoom, maxZoom) })),
    /** Zoom about a surface-relative point, so what is under it stays under
     *  it — the same anchoring ⌘-scroll uses. */
    zoomAt: (px: number, py: number, factor: number) =>
      setCamera((c) => {
        const zoom = clamp(c.zoom * factor, minZoom, maxZoom);
        const k = zoom / c.zoom;
        return { zoom, x: px - (px - c.x) * k, y: py - (py - c.y) * k };
      }),
    reset: () => setCamera({ x: 0, y: 0, zoom: 1 }),
    /** Screen point → canvas point. */
    toCanvas: (sx: number, sy: number, rect: DOMRect): { x: number; y: number } => ({
      x: (sx - rect.left - camera.x) / camera.zoom,
      y: (sy - rect.top - camera.y) / camera.zoom,
    }),
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(v, hi));
}
